import { Mail, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AssigneePermissionsEditor } from "@/components/claims/assignees/AssigneePermissionsEditor";
import type { ClaimAssignee, UpdateClaimAccessRequestBlockedModulesEnum } from "@/api/generated/client/models";
import { getBlockedModules } from "@/lib/claim-assignee-module-utils";

interface AssigneeEditDialogProps {
    assignee: ClaimAssignee | null;
    canWrite: boolean;
    isUpdatingAccess: boolean;
    onClose: () => void;
    onAccessLevelChange: (assignee: ClaimAssignee, nextLevel: "ro" | "rw") => Promise<void>;
    onModuleToggle: (
        assignee: ClaimAssignee,
        module: UpdateClaimAccessRequestBlockedModulesEnum,
        checked: boolean,
    ) => Promise<void>;
}

export function AssigneeEditDialog({
    assignee,
    canWrite,
    isUpdatingAccess,
    onClose,
    onAccessLevelChange,
    onModuleToggle,
}: AssigneeEditDialogProps) {
    return (
        <Dialog open={!!assignee} onOpenChange={(open) => !open && onClose()}>
            {assignee && (
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit assigned user</DialogTitle>
                        <DialogDescription>{assignee.name || assignee.email}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 rounded-md border bg-muted/20 p-3 text-sm">
                        <a href={`mailto:${assignee.email}`} className="flex min-w-0 items-center gap-2 text-muted-foreground hover:text-foreground">
                            <Mail className="h-4 w-4 shrink-0" />
                            <span className="truncate">{assignee.email}</span>
                        </a>
                        {assignee.phone ? (
                            <a href={`tel:${assignee.phone}`} className="flex min-w-0 items-center gap-2 text-muted-foreground hover:text-foreground">
                                <Phone className="h-4 w-4 shrink-0" />
                                <span className="truncate">{assignee.phone}</span>
                            </a>
                        ) : (
                            <p className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="h-4 w-4 shrink-0" />
                                Phone not provided
                            </p>
                        )}
                    </div>
                    <AssigneePermissionsEditor
                        assignee={assignee}
                        canWrite={canWrite}
                        isUpdatingAccess={isUpdatingAccess}
                        blockedModules={getBlockedModules(assignee)}
                        onAccessLevelChange={onAccessLevelChange}
                        onModuleToggle={onModuleToggle}
                        moduleGridClassName="grid grid-cols-2 gap-x-3 gap-y-2"
                    />
                </DialogContent>
            )}
        </Dialog>
    );
}